import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import CircularProgress from '@mui/material/CircularProgress';
import { useListMilestonesQuery } from "../../../generated";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useDownloadReport } from "../../../hooks/useDownloadReport";
import useNewrelic from "../../../hooks/useNewrelic";
import { Typography, DialogContent, DialogTitle, DialogActions, styled, Dialog } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from "@mui/lab/LoadingButton";

interface DownloadReportsProps {
  arn: string;
  workloadname: string;
  workloadid: string;
  workloadlens: string[];
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export default function DownloadReports({ arn, workloadname, workloadid, workloadlens }: DownloadReportsProps) {
  const { noticeError } = useNewrelic();
  const { downloadReport, loading } = useDownloadReport(arn);
  const [activeSpinner, setActiveSpinner] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setActiveSpinner(false);
  }
  const [loadingGenAI, setLoadingGenAI] = useState(false);
  const [loadingWellArchitected, setLoadingWellArchitected] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const { data } = useListMilestonesQuery({
    variables: { arn, workloadid },
    context: { apiName: "well_architected" },
    notifyOnNetworkStatusChange: true,
  });

  const handleDownload = async () => {
    const milestoneNumber = data?.listMilestones?.MilestoneSummaries?.[0]?.MilestoneNumber;
    if (!milestoneNumber) return;

    setActiveSpinner(true);
    if(workloadlens.length > 1){
      handleOpen();
    } else {
      try {
        await downloadReport(workloadname, workloadid, "wellarchitected", milestoneNumber);
      } catch (error) {
        noticeError(error, "Error downloading reports");
      } finally {
        setActiveSpinner(false);
      }
    }
   };

  const DownloadGenAI = async () => {
    const milestoneNumber = data?.listMilestones?.MilestoneSummaries?.[0]?.MilestoneNumber;
    if (!milestoneNumber) return;
    setDisabled(true);
    setLoadingGenAI(true);
    try {
      await downloadReport(workloadname, workloadid, "arn:aws:wellarchitected::aws:lens/genai", milestoneNumber);
    } catch (error) {
      noticeError(error, "Error downloading Gen AI Report");
    } finally {
      setDisabled(false);
      setLoadingGenAI(false);
    }
  }

  const DownloadWellArchitected = async () => {
    const milestoneNumber = data?.listMilestones?.MilestoneSummaries?.[0]?.MilestoneNumber;
    if (!milestoneNumber) return;
    setDisabled(true);
    setLoadingWellArchitected(true);
    try {
      await downloadReport(workloadname, workloadid, "wellarchitected", milestoneNumber);
    } catch (error) {
      noticeError(error, "Error downloading Well-Architected Report");
    } finally {
      setDisabled(false);
      setLoadingWellArchitected(false);
    }
  }

  return (
    <>
    <IconButton disabled={loading || activeSpinner} onClick={handleDownload} color="secondary">
      {activeSpinner ? <CircularProgress size={24} /> : <CloudDownloadOutlinedIcon />}
    </IconButton>
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        Download Lens Report
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography gutterBottom>
          There are two lens reports available for this workload. Please download which lens you would like.          
        </Typography>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          disabled={disabled}
          loading={loadingWellArchitected}
          onClick={DownloadWellArchitected} 
          type="submit" 
          variant="outlined" 
          sx={{
              width: 257,
              height: 40,
              "&:hover": {
                  color: "primary.main"
              }
          }}
          loadingIndicator={
            <CircularProgress color={"secondary"} size={22} />
          }
        >
          WellArchitected Lens
        </LoadingButton>
        <LoadingButton
          disabled={disabled}
          loading={loadingGenAI}
          onClick={DownloadGenAI} 
          type="submit" 
          variant="contained"
          sx={{
              width:  257,
              height: 40,
              bgcolor: "secondary.main",
              "&:hover": {
                  bgcolor:"secondary.main",
                  color: "secondary.contrastText"
              }
          }}
          loadingIndicator={
            <CircularProgress color={"secondary"} size={22} />
          }
        >
          Generative AI Lens
        </LoadingButton>
      </DialogActions>
    </BootstrapDialog>
    </>
  );
}
